"use client"

import * as React from "react"
import { ChevronRight, MoreHorizontal } from "lucide-react"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"
import { useShellTranslation } from "@/lib/shell/use-shell-translation"

// The landmark and the collapsed-crumbs label are read out on every admin
// page, so they speak the interface language (English outside the provider).
// A page that names its own landmark (aria-label) keeps it.
// min-w-0: the landmark may shrink inside the header row, so the crumbs' own
// caps decide what is cut rather than the header overflowing.
function Breadcrumb({ className, ...props }: React.ComponentProps<"nav">) {
  const { t } = useShellTranslation()
  return (
    <nav
      aria-label={t("shell.breadcrumb.label")}
      data-slot="breadcrumb"
      className={cn("min-w-0", className)}
      {...props}
    />
  )
}

// A compact trail (owner, 2 October 2026: "each breadcrumb size can be small"):
// the admin text size (13 px at the default root, written in rem so Clarity's
// larger root still grows it), tight gaps, and ONE line. It never wraps: each
// crumb is capped (BreadcrumbLink, BreadcrumbPage) and may shrink further when
// the header is narrow, so a deep page cuts its crumbs instead of growing.
function BreadcrumbList({ className, ...props }: React.ComponentProps<"ol">) {
  return (
    <ol
      data-slot="breadcrumb-list"
      className={cn(
        "flex min-w-0 flex-nowrap items-center gap-1 text-[0.8125rem] text-muted-foreground sm:gap-1.5",
        className
      )}
      {...props}
    />
  )
}

function BreadcrumbItem({ className, ...props }: React.ComponentProps<"li">) {
  return (
    <li
      data-slot="breadcrumb-item"
      className={cn("inline-flex min-w-0 items-center gap-1", className)}
      {...props}
    />
  )
}

/**
 * The words a crumb shows, for its `title`: the strings and numbers under
 * `node`, through any elements (an asChild <Link>, an icon beside the label).
 */
function crumbText(node: React.ReactNode): string {
  if (node === null || node === undefined || typeof node === "boolean") return ""
  if (typeof node === "string" || typeof node === "number") return String(node)
  if (Array.isArray(node)) return node.map(crumbText).join("")
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) return crumbText(node.props.children)
  return ""
}

/** The crumb's own `title`, else its full words, so a cut crumb reads whole on hover. */
function crumbTitle(title: string | undefined, children: React.ReactNode): string | undefined {
  if (title !== undefined) return title
  const text = crumbText(children).replace(/\s+/g, " ").trim()
  return text || undefined
}

// A crumb's label takes the direction of its own words (dir="auto"): an
// English entity name in an Arabic bar keeps its start when it is cut, and an
// Arabic one reads right to left in an English bar. Its place in the trail
// still follows the page. A page's own `dir` wins.
//
// Every crumb is capped with an ellipsis: a crumb on the way at 11 rem, the
// current page at 14 rem (a little more room). The full words stay in the DOM,
// so a screen reader reads them whole, and in `title` for a pointer's hover.
// A page's own `title`, or a cap of its own in className, wins.
function BreadcrumbLink({
  asChild,
  className,
  title,
  children,
  ...props
}: React.ComponentProps<"a"> & {
  asChild?: boolean
}) {
  const Comp = asChild ? Slot.Root : "a"

  return (
    <Comp
      data-slot="breadcrumb-link"
      dir="auto"
      title={crumbTitle(title, children)}
      className={cn("block max-w-[11rem] truncate transition-colors hover:text-foreground", className)}
      {...props}
    >
      {children}
    </Comp>
  )
}

function BreadcrumbPage({ className, title, children, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="breadcrumb-page"
      role="link"
      aria-disabled="true"
      aria-current="page"
      dir="auto"
      title={crumbTitle(title, children)}
      className={cn("block max-w-[14rem] truncate font-normal text-foreground", className)}
      {...props}
    >
      {children}
    </span>
  )
}

function BreadcrumbSeparator({
  children,
  className,
  ...props
}: React.ComponentProps<"li">) {
  return (
    <li
      data-slot="breadcrumb-separator"
      role="presentation"
      aria-hidden="true"
      className={cn("shrink-0 [&>svg]:size-3", className)}
      {...props}
    >
      {/* Points along the reading direction: mirrored in right-to-left. */}
      {children ?? <ChevronRight className="rtl-flip" />}
    </li>
  )
}

function BreadcrumbEllipsis({
  className,
  ...props
}: React.ComponentProps<"span">) {
  const { t } = useShellTranslation()
  return (
    <span
      data-slot="breadcrumb-ellipsis"
      role="presentation"
      aria-hidden="true"
      className={cn("flex size-9 items-center justify-center", className)}
      {...props}
    >
      <MoreHorizontal className="size-4" />
      <span className="sr-only">{t("shell.breadcrumb.more")}</span>
    </span>
  )
}

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
}
